import React, { useCallback, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Spinner } from "react-bootstrap";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import {
  downloadArquivoRespostaCampo,
  type DadosSindicatoDetalhe,
} from "../../../../../../services";
import NotificationToast from "../../../../../../components/NotificationToast/NotificationToast";
import type { SecaoDocumento } from "../../../../../../domain/shipping/models";
import ComprovanteEnvioDocumentacaoV2Corpo from "../ComprovanteEnvioDocumentacaoV2Corpo/ComprovanteEnvioDocumentacaoV2Corpo";
import { salvarPdfComprovanteEnvioV2 } from "../../utils/comprovanteEnvioV2Pdf";
import "./ConsultaEnvioDocumentacaoV2.css";

export type ConsultaEnvioDocumentacaoV2Props = {
  solicitacaoId: number;
  dadosSindicato: DadosSindicatoDetalhe | null;
  secoesObrigatorios: SecaoDocumento[];
  secoesBonus: SecaoDocumento[];
  secoesBonusExtra: SecaoDocumento[];
  enviadoEm: string | null;
  onVoltarLista: () => void;
  tituloLinha2?: string;
  afterDadosSindicato?: React.ReactNode;
  parecerPorSecaoId?: Record<string, (string | React.ReactElement)[]>;
};

const ConsultaEnvioDocumentacaoV2: React.FC<ConsultaEnvioDocumentacaoV2Props> = ({
  solicitacaoId,
  dadosSindicato,
  secoesObrigatorios,
  secoesBonus,
  secoesBonusExtra,
  enviadoEm,
  onVoltarLista,
  tituloLinha2 = "ENVIADA",
  afterDadosSindicato,
  parecerPorSecaoId,
}) => {
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingArquivoId, setDownloadingArquivoId] = useState<number | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type: "success" | "error";
  }>({ show: false, message: "", type: "error" });

  const isDownloadingArquivo = downloadingArquivoId !== null;

  const handleDownloadArquivo = useCallback(async (id: number, label: string) => {
    if (isDownloadingArquivo) return;

    try {
      setDownloadingArquivoId(id);
      await new Promise((resolve) => window.requestAnimationFrame(() => resolve(null)));
      await downloadArquivoRespostaCampo(id, label);
    } finally {
      setDownloadingArquivoId((current) => (current === id ? null : current));
    }
  }, [isDownloadingArquivo]);

  const handleDownloadPdfSolicitacao = async () => {
    const source = pdfContainerRef.current;
    if (!source) {
      setToast({
        show: true,
        type: "error",
        message: "Não foi possível gerar o PDF desta tela.",
      });
      return;
    }

    try {
      flushSync(() => {
        setDownloadingPdf(true);
        setExportingPdf(true);
      });
      await new Promise((resolve) => window.requestAnimationFrame(() => resolve(null)));
      await new Promise((resolve) => window.requestAnimationFrame(() => resolve(null)));
      await salvarPdfComprovanteEnvioV2(source, solicitacaoId);
    } catch (_error: unknown) {
      setToast({
        show: true,
        type: "error",
        message: "Não foi possível gerar o PDF da tela. Tente novamente.",
      });
    } finally {
      setExportingPdf(false);
      setDownloadingPdf(false);
    }
  };

  return (
    <>
      <ComprovanteEnvioDocumentacaoV2Corpo
        containerRef={pdfContainerRef}
        dadosSindicato={dadosSindicato}
        secoesObrigatorios={secoesObrigatorios}
        secoesBonus={secoesBonus}
        secoesBonusExtra={secoesBonusExtra}
        enviadoEm={enviadoEm}
        tituloLinha2={tituloLinha2}
        afterDadosSindicato={afterDadosSindicato}
        parecerPorSecaoId={parecerPorSecaoId}
        mapSecoesOptions={{
          downloadingArquivoId,
          isDownloadingArquivo,
          onDownloadArquivo: handleDownloadArquivo,
        }}
        headerActions={
          <>
            <button
              type="button"
              title="Voltar para a lista"
              aria-label="Voltar para a lista"
              className={`merit-voltar-seta ${exportingPdf ? "consulta-envio-hide-on-export" : ""}`}
              onClick={onVoltarLista}
            >
              <FaArrowLeft />
            </button>
            <button
              type="button"
              className={`btn btn-primary btn-sm consulta-envio-download-btn ${exportingPdf ? "consulta-envio-hide-on-export" : ""}`}
              onClick={() => void handleDownloadPdfSolicitacao()}
              title="Baixar PDF da solicitação enviada"
              aria-label="Baixar PDF da solicitação enviada"
              disabled={downloadingPdf}
            >
              {downloadingPdf ? (
                <Spinner animation="border" size="sm" variant="light" />
              ) : (
                <FaDownload size={13} />
              )}
              <span>{downloadingPdf ? "Gerando PDF..." : "Baixar PDF"}</span>
            </button>
          </>
        }
      />

      <NotificationToast
        show={toast.show}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        message={toast.message}
        type={toast.type}
      />
    </>
  );
};

export default ConsultaEnvioDocumentacaoV2;
